import { inject, Injectable } from '@angular/core';
import { CardDraft, CommentDraft, DraftType } from '@shared/api/api-loop/models';
import { CommentActionsService } from '@shared/modules/comments/components/services/comment-actions/comment-actions.service';
import { SharedTagActionsService } from '@shared/modules/shared-tag/data-access/shared-tag-actions/shared-tag-actions.service';
import { ConversationsActionService } from '@shared/modules/conversations/services/conversations-action.service';
import { ConversationChangeService } from '@shared/services/data/conversation-change/conversation-change.service';
import { SmartConversationsApiService } from '@shared/modules/conversations/services/conversations-api-cache/smart-conversations-api.service';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { EMPTY, merge, Observable } from 'rxjs';
import { ConversationChangeModel } from '@dta/shared/models-api-loop/conversation-card/conversation/conversation-change.model';
import { ConversationModel } from '@dta/shared/models-api-loop/conversation-card/conversation/conversation.model';
import { StaticSharedTagIds } from '@dta/shared/models-api-loop/shared-tag/shared-tag.model';

const MAX_TRACKED_DRAFTS = 200;

/**
 * The composer that creates a draft may live in a different Electron window than the one
 * rendering the conversation, and only comment/shared-tag subjects are relayed over the IPC
 * bridge — the synced `ConversationModel` (sharedDrafts/privateDrafts) lags several seconds
 * behind. This service turns a draft create/delete into an optimistic `ConversationChangeModel`
 * and re-emits the patched conversation immediately, so the list icon, header and detail view
 * (and `openExistingDraft$`) don't wait on sync. `ConversationChangeService` keeps re-applying
 * the recorded change to every synced conversation for its TTL, so a stale sync can't undo it.
 */
@Injectable({ providedIn: 'root' })
export class DraftConversationChangesService {
  private readonly commentActionsService: CommentActionsService = inject(CommentActionsService);
  private readonly sharedTagActionsService: SharedTagActionsService = inject(SharedTagActionsService);
  private readonly conversationsActionService: ConversationsActionService = inject(ConversationsActionService);
  private readonly conversationChangeService: ConversationChangeService = inject(ConversationChangeService);
  private readonly smartConversationsApiService: SmartConversationsApiService = inject(SmartConversationsApiService);

  // draft card id -> conversation card id, so a later delete of the same draft can find its conversation.
  private readonly conversationIdByDraftId: Map<string, string> = new Map();

  init$(): Observable<unknown> {
    return merge(
      this.observeDraftCreated$().pipe(map(() => undefined)),
      this.observeDraftDeleted$().pipe(map(() => undefined))
    );
  }

  private observeDraftCreated$(): Observable<unknown> {
    return this.commentActionsService.draftUpserted$.pipe(
      concatMap((draft: CommentDraft) => {
        const conversationCardId = draft.draftOnCardId?.replace(/-copy1T$/, '');
        const draftCardId = draft.parent?.id;

        if (!conversationCardId || !draftCardId) {
          return EMPTY;
        }

        // Subsequent autosaves of the same draft must not re-emit — conversationsUpdated$ has heavy consumers.
        if (this.conversationIdByDraftId.has(draftCardId)) {
          return EMPTY;
        }

        this.rememberDraft(draftCardId, conversationCardId);

        // The composer sets editorState only for shared drafts.
        const draftType: DraftType = draft.draftType ?? (draft.editorState ? DraftType.SHARED : DraftType.PRIVATE);
        const cardDraft: CardDraft = {
          $type: 'CardDraft',
          id: draftCardId,
          draftType,
          sendLaterDate: draft.sendLaterDateTime
        };
        const change = ConversationChangeModel.createDraftChange({
          cardId: conversationCardId,
          action: 'add',
          draft: cardDraft,
          draftType
        });

        return this.applyAndEmit$(conversationCardId, change);
      })
    );
  }

  private observeDraftDeleted$(): Observable<unknown> {
    return this.sharedTagActionsService.sharedTagsUpdated$.pipe(
      concatMap(sharedTagsUpdated => {
        const isDeletedDraftTag =
          sharedTagsUpdated?.parent?.$type === 'CardDraft' &&
          !!sharedTagsUpdated.tags?.resources?.some(tag => tag.id === StaticSharedTagIds.DELETED_ID);

        if (!isDeletedDraftTag) {
          return EMPTY;
        }

        const draftCardId = sharedTagsUpdated.parent.id;
        const conversationCardId = this.conversationIdByDraftId.get(draftCardId);

        // The draft was not created in this window — sync will remove it from the conversation.
        if (!conversationCardId) {
          return EMPTY;
        }

        this.conversationIdByDraftId.delete(draftCardId);

        const change = ConversationChangeModel.createDraftChange({
          cardId: conversationCardId,
          action: 'remove',
          draft: { $type: 'CardDraft', id: draftCardId },
          draftType: DraftType.PRIVATE
        });

        return this.applyAndEmit$(conversationCardId, change);
      })
    );
  }

  private applyAndEmit$(conversationCardId: string, change: ConversationChangeModel): Observable<unknown> {
    return this.conversationChangeService.saveAll([change]).pipe(
      concatMap(() =>
        this.smartConversationsApiService.getSingle(conversationCardId).pipe(
          take(1),
          catchError(() => EMPTY)
        )
      ),
      map(conversation => {
        const patched = new ConversationModel({ ...conversation });
        this.conversationChangeService.applyConversationChange([patched]);
        this.conversationsActionService.conversationsUpdated$.next([patched]);
        return patched;
      }),
      catchError(() => EMPTY)
    );
  }

  private rememberDraft(draftCardId: string, conversationCardId: string): void {
    this.conversationIdByDraftId.set(draftCardId, conversationCardId);

    if (this.conversationIdByDraftId.size > MAX_TRACKED_DRAFTS) {
      const oldestDraftId = this.conversationIdByDraftId.keys().next().value;
      this.conversationIdByDraftId.delete(oldestDraftId);
    }
  }
}
